import { useEffect, useState } from 'react';

import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  makeStyles,
  Paper,
  Radio,
  Typography,
} from '@material-ui/core';
import Security from '@material-ui/icons/Security';
import VpnKey from '@material-ui/icons/Update';
import Warning from '@material-ui/icons/Warning';
import clsx from 'clsx';
import { useModal } from 'react-modal-hook';

import { ReactComponent as PinCode } from 'assets/images/pin-code.svg';
import { ReactComponent as QRCodeIcon } from 'assets/images/QR Code.svg';
import DialogHeader from 'components/DialogHeader';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { CanalInterface, TipoConexao } from 'store/reducers/canais';

import ModalPinCode from './components/ModalPinCode';
import ModalQRCode from './components/ModalQRCode';

const useStyles = makeStyles(({ palette, spacing }) => ({
  dialogContent: {
    padding: spacing(0, 3, 3, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
    flexGrow: 1,
  },
  paper: {
    width: spacing(45),
    height: spacing(37.5),
    paddingInline: spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(palette.background.tertiary, 0.5),
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
  },
  optionCard: {
    border: `1px solid ${palette.divider}`,
    borderRadius: spacing(1),
    padding: spacing(2.5, 3),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2.5),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: palette.background.paper,
    '&:hover': {
      borderColor: palette.type === 'light' ? '#9E9E9E' : '#757575',
    },
  },
  optionCardSelected: {
    borderColor: palette.primary.main,
    backgroundColor: alpha(palette.primary.main, 0.05),
    '&:hover': {
      borderColor: palette.primary.main,
    },
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.background.tertiary,
    color: palette.text.secondary,
  },
  iconContainerSelected: {
    backgroundColor: palette.primary.main,
    color: palette.primary.contrastText,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
  },
  optionTitle: {
    fontWeight: 700,
    color: palette.text.primary,
  },
  optionDescription: {
    color: palette.text.secondary,
    marginTop: spacing(0.5),
  },
  badge: {
    backgroundColor: alpha(palette.success.main, 0.15),
    color:
      palette.type === 'light' ? palette.success.dark : palette.success.light,
    padding: spacing(0.2, 1),
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
  },
  warningBox: {
    backgroundColor: alpha(palette.warning.main, 0.15),
    borderLeft: `4px solid ${palette.warning.main}`,
    borderRadius: 8,
    padding: spacing(2, 2.5),
    display: 'flex',
    gap: spacing(2),
    marginTop: spacing(1),
  },
  warningIcon: {
    color: palette.warning.main,
  },
  warningTitle: {
    fontWeight: 700,
    color:
      palette.type === 'light' ? palette.warning.dark : palette.warning.light,
    marginBottom: spacing(1),
  },
  warningParagraph: {
    color: palette.text.primary,
    fontSize: 13,
    lineHeight: 1.5,
  },
}));

type ModalAutorizarConexaoCanalProps = {
  open: boolean;
  onClose: () => void;
  canal: CanalInterface | undefined;
};

export default function ModalAutorizarConexaoCanal(
  props: ModalAutorizarConexaoCanalProps,
) {
  const { open, onClose, canal } = props;
  const classes = useStyles();

  const [step, setStep] = useState<'PROCESSING_CHOICE' | 'CONNECTION_METHOD'>(
    'PROCESSING_CHOICE',
  );
  const [isProcessarMensagensAntigas, setIsProcessarMensagensAntigas] =
    useState(false);

  useEffect(() => {
    if (open) {
      setStep('PROCESSING_CHOICE');
      setIsProcessarMensagensAntigas(false);
    }
  }, [open]);

  const [showModalQRCode, hideModalQRCode] = useModal(
    ({ in: open }) => (
      <ModalQRCode
        open={open}
        onClose={hideModalQRCode}
        canal={canal}
        isProcessarMensagensAntigas={isProcessarMensagensAntigas}
      />
    ),
    [canal, isProcessarMensagensAntigas],
  );

  const handleClose = () => {
    onClose();
  };

  const [showModalPinCode, hideModalPinCode] = useModal(
    ({ in: open }) => (
      <ModalPinCode
        open={open}
        onClose={hideModalPinCode}
        canal={canal}
        isProcessarMensagensAntigas={isProcessarMensagensAntigas}
      />
    ),
    [canal, isProcessarMensagensAntigas],
  );

  const handleScan = (tipoConexao: TipoConexao) => {
    if (canal) {
      const { canalId } = canal;
      const data = { canalId, action: 'enter' };

      socket.emit(socketEvents.EMIT_ROOM_QR_CODE, data);
      socket.emit(socketEvents.EMIT_RECONECTAR_CANAL, {
        canalId,
        tipoConexao,
        isProcessarMensagensAntigas,
      });
    }

    handleClose(); // fecha o modal inicial

    if (tipoConexao === TipoConexao.WHATSAPP_QRCODE) {
      showModalQRCode(); // abre o modal de qr code
    } else {
      showModalPinCode(); // abre o modal de pin code
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <DialogHeader
        title={
          <Box component="span" style={{ fontWeight: 700 }}>
            Conectar canal
          </Box>
        }
        onBack={
          step === 'CONNECTION_METHOD'
            ? () => setStep('PROCESSING_CHOICE')
            : undefined
        }
        onClose={onClose}
      />

      {step === 'PROCESSING_CHOICE' ? (
        <DialogContent className={classes.dialogContent}>
          <Typography
            variant="body2"
            style={{ color: '#757575', marginBottom: 8 }}
          >
            Antes de conectar, escolha quais mensagens (recebidas e enviadas)
            este canal deve processar.
          </Typography>

          <Box className={classes.optionsContainer}>
            <Box
              className={clsx(classes.optionCard, {
                [classes.optionCardSelected]: !isProcessarMensagensAntigas,
              })}
              onClick={() => setIsProcessarMensagensAntigas(false)}
            >
              <Box
                className={clsx(classes.iconContainer, {
                  [classes.iconContainerSelected]: !isProcessarMensagensAntigas,
                })}
              >
                <Security />
              </Box>
              <Box className={classes.textContainer}>
                <Box className={classes.titleRow}>
                  <Typography variant="body1" className={classes.optionTitle}>
                    Processar apenas mensagens novas
                  </Typography>
                  <Box className={classes.badge}>Recomendado</Box>
                </Box>
                <Typography
                  variant="body2"
                  className={classes.optionDescription}
                >
                  Somente as mensagens recebidas e enviadas a partir do momento
                  desta conexão serão processadas. Mensagens anteriores serão
                  ignoradas.
                </Typography>
              </Box>
              <Radio checked={!isProcessarMensagensAntigas} color="primary" />
            </Box>

            <Box
              className={clsx(classes.optionCard, {
                [classes.optionCardSelected]: isProcessarMensagensAntigas,
              })}
              onClick={() => setIsProcessarMensagensAntigas(true)}
            >
              <Box
                className={clsx(classes.iconContainer, {
                  [classes.iconContainerSelected]: isProcessarMensagensAntigas,
                })}
              >
                <VpnKey />
              </Box>
              <Box className={classes.textContainer}>
                <Box className={classes.titleRow}>
                  <Typography variant="body1" className={classes.optionTitle}>
                    Processar também as mensagens pendentes
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  className={classes.optionDescription}
                >
                  Processa todas as mensagens recebidas e enviadas desde a data
                  de ativação deste canal, incluindo as que ficaram pendentes.
                </Typography>
              </Box>
              <Radio checked={isProcessarMensagensAntigas} color="primary" />
            </Box>
          </Box>

          {isProcessarMensagensAntigas && (
            <Box className={classes.warningBox}>
              <Box style={{ marginTop: 2 }}>
                <Warning className={classes.warningIcon} />
              </Box>
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" className={classes.warningTitle}>
                  Atenção: opção com alto risco de banimento
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.warningParagraph}
                >
                  Não recomendamos esta opção se o seu número já foi banido
                  alguma vez ou se o canal ficou muito tempo desconectado da
                  plataforma. Processar de uma só vez um grande volume de
                  mensagens antigas (recebidas e enviadas) pode ser interpretado
                  como comportamento suspeito.
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.warningParagraph}
                  style={{ marginTop: 12 }}
                >
                  Isso ocorre porque são{' '}
                  <strong>políticas e regras da Meta</strong> (responsável pelo
                  WhatsApp), e não da nossa plataforma. O uso indevido pode
                  levar ao bloqueio definitivo do seu número.
                </Typography>
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" mt="auto" pt={2} mb={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep('CONNECTION_METHOD')}
            >
              Continuar
            </Button>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2">
            Selecione uma forma de conectar:
          </Typography>

          <Grid
            container
            spacing={2}
            justifyContent="center"
            style={{ overflow: 'auto' }}
          >
            <Grid item>
              <Option
                icon={<QRCodeIcon />}
                title="QR-Code"
                description="Será gerado um QR-Code único para ser lido pelo WhatsApp."
                onClick={() => handleScan(TipoConexao.WHATSAPP_QRCODE)}
              />
            </Grid>

            <Grid item>
              <Option
                icon={<PinCode />}
                title="Código de Verificação"
                description="Será exibido um código contendo letras e números para ser adicionado no WhatsApp."
                onClick={() => handleScan(TipoConexao.WHATSAPP_CODE)}
              />
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Dialog>
  );
}

interface OptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
}

function Option({ icon, title, description, onClick }: OptionProps) {
  const classes = useStyles();

  return (
    <Paper elevation={0} className={classes.paper}>
      <Box
        minHeight={120}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {icon}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        mt={2}
      >
        <Typography variant="body2" style={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="caption" style={{ fontWeight: 400, marginTop: 4 }}>
          {description}
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: 24 }}
        onClick={onClick}
      >
        Selecionar
      </Button>
    </Paper>
  );
}
